import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

export const useRiskAnalysisStore = defineStore('riskAnalysis', () => {
  // Core state
  const loading = ref(false)
  const error = ref(null)
  const riskAnalysis = ref(null)
  
  // Computed properties for flattened API structure
  const overallRiskLevel = computed(() => riskAnalysis.value?.overallRiskLevel || 'unknown')
  const overallRiskSummary = computed(() => riskAnalysis.value?.overallRiskSummary || '')
  const categoryRisks = computed(() => riskAnalysis.value?.categoryRisks || [])
  
  const totalFindings = computed(() => {
    return categoryRisks.value.reduce((total, category) => {
      return total + (category.findings?.length || 0)
    }, 0)
  })
  
  const availableCategories = computed(() => {
    return categoryRisks.value.map(category => ({
      id: category.categoryKey,
      name: category.categoryName,
      findingsCount: category.findings?.length || 0,
      findings: category.findings || []
    }))
  })
  
  const overallRiskScore = computed(() => riskAnalysis.value?.overallRiskScore || 0)
  
  const allFindings = computed(() => {
    return categoryRisks.value.flatMap(category =>
      (category.findings || []).map(finding => ({
        ...finding,
        categoryName: category.categoryName,
        categoryKey: category.categoryKey
      }))
    )
  })
  
  // Check if risk analysis exists and is completed
  const hasRiskAnalysis = computed(() => {
    return riskAnalysis.value != null && 
           (riskAnalysis.value.status === 'completed' || 
            riskAnalysis.value.categoryRisks?.length > 0)
  })
  
  // Risk level utilities
  const getRiskLevelColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      minimal: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    }
    return colors[level] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
  }
  
  const getRiskLevelIcon = (level) => {
    const icons = {
      critical: '🚨',
      high: '⚠️',
      medium: '⚡',
      low: '📋',
      minimal: '✅'
    }
    return icons[level] || '❓'
  }

  /**
   * Generate risk analysis for a session
   */
  async function generateRiskAnalysis(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Generating risk analysis for session:', sessionId)
      
      const response = await api.post(`/analysis/generate/${sessionId}`, {
        sessionId
      })
      
      console.log('Risk analysis generation response:', response.data)
      
      // Parse response structure (matches API docs structure)
      if (response.data?.success && response.data?.riskAnalysis) {
        const apiData = response.data
        
        // Flatten risk analysis to root level for component access
        const riskData = {
          // Spread riskAnalysis fields
          ...apiData.riskAnalysis,
          
          // Add metadata at root level
          metadata: apiData.metadata || {},
          summary: apiData.summary || {},
          
          // Add session tracking
          sessionId,
          projectId,
          success: apiData.success
        }
        
        console.log('Risk analysis generated successfully:', {
          overallRiskLevel: riskData.overallRiskLevel,
          categoryCount: riskData.categoryRisks?.length || 0,
          totalFindings: apiData.metadata?.totalFindings || 0
        })
        
        // Store the risk analysis data
        riskAnalysis.value = riskData
        
        return riskData
      }
      
      // Store the response data even if structure is unexpected
      riskAnalysis.value = response.data
      return response.data
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate risk analysis'
      const errorCode = err.response?.data?.code
      
      console.error('Risk analysis generation error:', {
        status: err.response?.status,
        code: errorCode,
        error: errorMessage,
        sessionId
      })
      
      // Preserve error code for specific error handling
      error.value = { message: errorMessage, code: errorCode }
      
      throw err // Throw original error to preserve response data
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Get existing risk analysis for a session
   */
  async function getRiskAnalysis(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Fetching risk analysis for session:', sessionId)
      
      const response = await api.get(`/analysis/${sessionId}`)
      
      console.log('Risk analysis response:', response.data)
      
      // Parse response structure
      if (response.data?.success && response.data?.riskAnalysis) {
        const apiData = response.data
        
        // Flatten risk analysis to root level
        const riskData = {
          ...apiData.riskAnalysis,
          metadata: apiData.metadata || {},
          summary: apiData.summary || {},
          sessionId,
          projectId,
          success: apiData.success,
          fetchedAt: new Date().toISOString()
        }
        
        console.log('Risk analysis loaded:', {
          overallRiskLevel: riskData.overallRiskLevel,
          categoryCount: riskData.categoryRisks?.length || 0,
          totalFindings: apiData.summary?.totalFindings || 0
        })
        
        // Store the risk analysis data
        riskAnalysis.value = riskData
        
        return riskData
      }
      
      // Store the response data even if structure is unexpected
      riskAnalysis.value = response.data
      return response.data
    } catch (err) {
      const status = err.response?.status
      const errorData = err.response?.data
      
      // Handle 404 - no risk analysis exists yet
      if (status === 404) {
        console.log('No risk analysis found for session:', sessionId)
        return {
          exists: false,
          sessionId,
          projectId,
          message: 'No risk analysis generated yet'
        }
      }
      
      const errorMessage = errorData?.error || err.message || 'Failed to get risk analysis'
      console.error('Risk analysis fetch error:', {
        status,
        code: errorData?.code,
        error: errorMessage,
        sessionId
      })
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Delete risk analysis for a session
   */
  async function deleteRiskAnalysis(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Project ID and Session ID are required')
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Deleting risk analysis for session:', sessionId)
      
      await api.delete(`/analysis/${sessionId}`)
      
      // Clear stored risk analysis
      riskAnalysis.value = null
      
      console.log('Risk analysis deleted successfully')
      return { success: true }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete risk analysis'
      console.error('Risk analysis delete error:', errorMessage)
      error.value = errorMessage
      throw new Error(errorMessage)
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    loading,
    error,
    riskAnalysis,
    
    // Computed properties
    overallRiskLevel,
    overallRiskSummary,
    overallRiskScore,
    categoryRisks,
    totalFindings,
    availableCategories,
    allFindings,
    hasRiskAnalysis,
    
    // Utility functions
    getRiskLevelColor,
    getRiskLevelIcon,
    
    // API functions
    generateRiskAnalysis,
    getRiskAnalysis,
    deleteRiskAnalysis
  }
})
